<template>
  <div>
    <div class="d-flex justify-content-between align-items-center mb-4">
      <h6 class="mb-0">Convênios do Paciente</h6>
      <button type="button" class="btn btn-sm btn-primary" @click="openAddModal">
        <i class="bi bi-plus-circle"></i> Adicionar Convênio
      </button>
    </div>

    <div v-if="loading" class="text-center py-4">
      <span class="spinner-border spinner-border-sm"></span>
      <span class="ms-2">Carregando...</span>
    </div>

    <div v-else-if="insurances.length === 0" class="text-center py-4 text-muted">
      <p>Nenhum convênio cadastrado para este paciente.</p>
    </div>

    <div v-else class="table-responsive">
      <table class="table table-hover">
        <thead>
          <tr>
            <th>Convênio</th>
            <th>Carteirinha</th>
            <th>Plano</th>
            <th>Vigência</th>
            <th>Status</th>
            <th class="text-center">Ações</th>
          </tr>
        </thead>
        <tbody>
          <tr v-for="insurance in insurances" :key="insurance.id_insurance">
            <td>
              <strong>{{ insurance.insurance_name }}</strong>
              <br>
              <small class="text-muted">ANS: {{ insurance.code_ans }}</small>
            </td>
            <td>{{ insurance.card_number || '-' }}</td>
            <td>{{ insurance.plan_name || '-' }}</td>
            <td>
              <span v-if="insurance.valid_from || insurance.valid_until">
                {{ formatDate(insurance.valid_from) }} até {{ formatDate(insurance.valid_until) }}
              </span>
              <span v-else>-</span>
            </td>
            <td>
              <span :class="['badge', insurance.active ? 'badge-success' : 'badge-danger']">
                {{ insurance.active ? 'Ativo' : 'Inativo' }}
              </span>
            </td>
            <td class="text-center">
              <div class="d-flex justify-content-center gap-1">
                <button type="button" class="btn btn-sm btn-icon btn-bg-light btn-active-color-primary"
                        @click="editInsurance(insurance)"
                        title="Editar">
                  <i class="bi bi-pencil-square"></i>
                </button>
                <button type="button" class="btn btn-sm btn-icon btn-bg-light btn-active-color-danger"
                        @click="deleteInsurance(insurance)"
                        title="Remover">
                  <i class="bi bi-trash"></i>
                </button>
              </div>
            </td>
          </tr>
        </tbody>
      </table>
    </div>

    <!-- Modal Add/Edit Insurance -->
    <div class="modal fade" tabindex="-1" id="modal_patient_insurance" data-bs-keyboard="false" data-bs-backdrop="static">
      <div class="modal-dialog">
        <form @submit.prevent="submitForm">
          <div class="modal-content">
            <div class="modal-header">
              <h5 class="modal-title">{{ editingInsurance ? 'Editar Convênio' : 'Adicionar Convênio' }}</h5>
              <button type="button" class="btn-close" data-bs-dismiss="modal" aria-label="Fechar"></button>
            </div>
            <div class="modal-body">
              <div class="mb-3">
                <label class="form-label required">Convênio</label>
                <Dropdown 
                  :options="availableInsurances"
                  v-model="form.id_insurance"
                  :optionLabel="(insurance) => `${insurance.code_ans} - ${insurance.insurance_name}`"
                  optionValue="id_insurance"
                  :filter="true"
                  :filterFields="['code_ans', 'insurance_name']"
                  :disabled="editingInsurance"
                  appendTo="self"
                  empty-message="Nenhuma opção"
                  empty-filter-message="Nenhuma opção encontrada"
                  class="form-control form-control-solid w-full md:w-14rem form-control-sm"
                  placeholder="Selecione um convênio"
                  :showClear="true"
                  style="display: flex; padding: 0.35rem 0.75rem"
                  required
                />
                <div v-if="errors.id_insurance" class="text-danger small">{{ errors.id_insurance[0] }}</div>
              </div>

              <div class="mb-3">
                <label class="form-label">Número da Carteirinha</label>
                <input type="text" class="form-control form-control-solid"
                       v-model="form.card_number"
                       v-mask="'#### #### #### #### ####'"
                       placeholder="Ex: 1234 5678 9012 3456"
                       maxlength="50">
                <div v-if="errors.card_number" class="text-danger small">{{ errors.card_number[0] }}</div>
              </div>

              <div class="mb-3">
                <label class="form-label">Nome do Plano</label>
                <input type="text" class="form-control form-control-solid"
                       v-model="form.plan_name"
                       placeholder="Ex: Plano Básico"
                       maxlength="255">
                <div v-if="errors.plan_name" class="text-danger small">{{ errors.plan_name[0] }}</div>
              </div>

              <div class="row">
                <div class="col-md-6 mb-3">
                  <label class="form-label">Data de Início</label>
                  <input type="date" class="form-control form-control-solid"
                         v-model="form.valid_from"
                         @change="onValidFromChange">
                  <div v-if="errors.valid_from" class="text-danger small">{{ errors.valid_from[0] }}</div>
                </div>
                <div class="col-md-6 mb-3">
                  <label class="form-label">Data de Término</label>
                  <input type="date" class="form-control form-control-solid"
                         v-model="form.valid_until"
                         :min="form.valid_from"
                         @change="validateValidUntil">
                  <div v-if="errors.valid_until" class="text-danger small">{{ errors.valid_until[0] }}</div>
                  <div v-if="validUntilError" class="text-danger small">{{ validUntilError }}</div>
                </div>
              </div>

              <div class="mb-3">
                <div class="form-check form-switch">
                  <input class="form-check-input" type="checkbox" v-model="form.active" id="insurance_active">
                  <label class="form-check-label" for="insurance_active">
                    Ativo
                  </label>
                </div>
              </div>
            </div>
            <div class="modal-footer">
              <button type="button" class="btn btn-secondary" data-bs-dismiss="modal">Cancelar</button>
              <button type="submit" class="btn btn-primary">{{ editingInsurance ? 'Atualizar' : 'Adicionar' }}</button>
            </div>
          </div>
        </form>
      </div>
    </div>
  </div>
</template>

<script>
import http from "../../helpers/http";
import Alert from "../../helpers/alert";
import { mask } from "vue-the-mask";
import Dropdown from "primevue/dropdown";

export default {
  name: "PatientInsurances",
  components: {
    Dropdown,
  },
  directives: {
    mask,
  },
  props: {
    patient: {
      type: Object,
      required: true
    },
    place: {
      type: Object,
      required: true
    }
  },
  data() {
    return {
      loading: false,
      insurances: [],
      availableInsurances: [],
      editingInsurance: null,
      form: {
        id_insurance: '',
        card_number: '',
        plan_name: '',
        valid_from: '',
        valid_until: '',
        active: true
      },
      errors: {},
      validUntilError: ''
    }
  },
  mounted() {
    this.loadInsurances();
    this.loadAvailableInsurances();
  },
  methods: {
    loadInsurances() {
      if (!this.patient?.id_patient) return;
      
      this.loading = true;
      http.get(`/places/${this.place.id_place}/patients/${this.patient.id_patient}/insurances`)
        .then((response) => {
          this.insurances = response.data.data || [];
        })
        .catch((e) => {
          console.error('Error loading insurances:', e);
          Alert.toastError('Erro ao carregar convênios.');
        })
        .finally(() => {
          this.loading = false;
        });
    },
    loadAvailableInsurances() {
      http.get(`/places/${this.place.id_place}/insurances`)
        .then((response) => {
          this.availableInsurances = response.data || [];
        })
        .catch((e) => {
          console.error('Error loading available insurances:', e);
        });
    },
    openAddModal() {
      this.editingInsurance = null;
      this.form = {
        id_insurance: '',
        card_number: '',
        plan_name: '',
        valid_from: '',
        valid_until: '',
        active: true
      };
      this.errors = {};
      const modal = bootstrap.Modal.getOrCreateInstance(document.getElementById('modal_patient_insurance'));
      modal.show();
    },
    editInsurance(insurance) {
      this.editingInsurance = insurance;
      
      // Formatar datas para o formato YYYY-MM-DD (necessário para input type="date")
      let validFrom = '';
      let validUntil = '';
      
      if (insurance.valid_from) {
        const fromDate = new Date(insurance.valid_from);
        if (!isNaN(fromDate.getTime())) {
          const year = fromDate.getFullYear();
          const month = String(fromDate.getMonth() + 1).padStart(2, '0');
          const day = String(fromDate.getDate()).padStart(2, '0');
          validFrom = `${year}-${month}-${day}`;
        }
      }
      
      if (insurance.valid_until) {
        const untilDate = new Date(insurance.valid_until);
        if (!isNaN(untilDate.getTime())) {
          const year = untilDate.getFullYear();
          const month = String(untilDate.getMonth() + 1).padStart(2, '0');
          const day = String(untilDate.getDate()).padStart(2, '0');
          validUntil = `${year}-${month}-${day}`;
        }
      }
      
      this.form = {
        id_insurance: insurance.id_insurance,
        card_number: insurance.card_number || '',
        plan_name: insurance.plan_name || '',
        valid_from: validFrom,
        valid_until: validUntil,
        active: insurance.active !== undefined ? insurance.active : true
      };
      this.errors = {};
      this.validUntilError = '';
      const modal = bootstrap.Modal.getOrCreateInstance(document.getElementById('modal_patient_insurance'));
      modal.show();
    },
    submitForm() {
      this.errors = {};
      this.validUntilError = '';
      
      // Validar data de término antes de enviar
      if (this.form.valid_from && this.form.valid_until) {
        const startDate = new Date(this.form.valid_from);
        const endDate = new Date(this.form.valid_until);
        
        if (endDate < startDate) {
          this.validUntilError = 'A data de término não pode ser menor que a data de início.';
          return;
        }
      }
      
      const url = this.editingInsurance
        ? `/places/${this.place.id_place}/patients/${this.patient.id_patient}/insurances/${this.editingInsurance.id_insurance}`
        : `/places/${this.place.id_place}/patients/${this.patient.id_patient}/insurances`;
      
      const method = this.editingInsurance ? 'put' : 'post';
      
      http[method](url, this.form)
        .then(() => {
          Alert.toastSuccess(this.editingInsurance ? 'Convênio atualizado com sucesso.' : 'Convênio adicionado com sucesso.');
          const modal = bootstrap.Modal.getInstance(document.getElementById('modal_patient_insurance'));
          modal.hide();
          this.loadInsurances();
        })
        .catch((e) => {
          if (e.response?.data?.errors) {
            this.errors = e.response.data.errors;
          } else {
            Alert.toastError(e.response?.data?.message || 'Erro ao salvar convênio.');
          }
        });
    },
    deleteInsurance(insurance) {
      if (!confirm(`Tem certeza que deseja remover o convênio ${insurance.insurance_name}?`)) {
        return;
      }
      
      http.delete(`/places/${this.place.id_place}/patients/${this.patient.id_patient}/insurances/${insurance.id_insurance}`)
        .then(() => {
          Alert.toastSuccess('Convênio removido com sucesso.');
          this.loadInsurances();
        })
        .catch((e) => {
          Alert.toastError(e.response?.data?.message || 'Erro ao remover convênio.');
        });
    },
    formatDate(date) {
      if (!date) return '-';
      return this.date(date).format('DD/MM/YYYY');
    },
    onValidFromChange() {
      if (this.form.valid_from) {
        // Adicionar 1 ano à data de início
        const startDate = new Date(this.form.valid_from);
        startDate.setFullYear(startDate.getFullYear() + 1);
        
        // Formatar para YYYY-MM-DD
        const year = startDate.getFullYear();
        const month = String(startDate.getMonth() + 1).padStart(2, '0');
        const day = String(startDate.getDate()).padStart(2, '0');
        this.form.valid_until = `${year}-${month}-${day}`;
        
        // Limpar erro se existir
        this.validUntilError = '';
      }
    },
    validateValidUntil() {
      if (this.form.valid_from && this.form.valid_until) {
        const startDate = new Date(this.form.valid_from);
        const endDate = new Date(this.form.valid_until);
        
        if (endDate < startDate) {
          this.validUntilError = 'A data de término não pode ser menor que a data de início.';
        } else {
          this.validUntilError = '';
        }
      }
    },
    date: moment
  }
}
</script>

<style scoped>
.required::after {
  content: ' *';
  color: red;
}
</style>


